import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Tab, Tabs } from '@mui/material';
import { toast } from 'react-toastify';
import { FiAlertCircle, FiCheckCircle, FiSave } from 'react-icons/fi';
import { fetchWithAuth, getTenantVoiceNumbers } from '../../../api/backendApi';
import PhoneNumbersTab, { formatE164Display, numberOptionLabel } from './PhoneNumbersTab';

const CARD_CLASS = 'bg-white border border-gray-300 shadow-md rounded-lg p-6';
const CONTROL_BASE_CLASS = 'block w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed';
const INPUT_CLASS = CONTROL_BASE_CLASS;
const SELECT_CLASS = CONTROL_BASE_CLASS;
const TEXTAREA_MONO_CLASS = `${CONTROL_BASE_CLASS} leading-relaxed font-mono`;
const TOGGLE_ROW_CLASS = 'flex items-center gap-3 py-3 px-4 border border-gray-300 rounded-md bg-gray-50';
const HIGHLIGHT_TOGGLE_CLASS = 'flex items-center gap-3 py-3 px-4 border border-blue-300 rounded-md bg-white';
const NESTED_CALLOUT_CLASS = 'grid grid-cols-2 gap-y-4 gap-x-6 sm:grid-cols-4 lg:grid-cols-4 rounded-md border border-blue-300 bg-blue-50/70 p-4';

const PSTN_DEFAULTS = {
  voiceMode: 'none',
  enableOutboundPstn: false,
  callerIdFrom: '',
  defaultCountryCode: 'US',
  addPrefixOutbound: '+1',
  intlAllowed: false,
  e911Enabled: false,
  blockedPrefixes: '',
  emergencyPatterns: '',
  maxCallDuration: 300,
  rateLimit: 60,
};

const toStringList = (value, fallback) => {
  if (typeof value === 'string') {
    return value.split('\n').map((s) => s.trim()).filter(Boolean);
  }
  if (Array.isArray(value)) return value.filter(Boolean);
  if (Array.isArray(fallback)) return fallback.filter(Boolean);
  return [];
};

const toFormValues = (data = {}) => ({
  ...PSTN_DEFAULTS,
  voiceMode: data.voiceMode || (data.enableOutboundPstn && data.callerIdFrom ? 'shared' : 'none'),
  enableOutboundPstn: Boolean(data.enableOutboundPstn),
  callerIdFrom: data.callerIdFrom || '',
  defaultCountryCode: data.defaultCountryCode || 'US',
  addPrefixOutbound: data.addPrefixOutbound || '+1',
  intlAllowed: Boolean(data.intlAllowed),
  e911Enabled: Boolean(data.e911Enabled),
  maxCallDuration: typeof data.maxCallDuration === 'number' ? data.maxCallDuration : 300,
  rateLimit: typeof data.rateLimit === 'number' ? data.rateLimit : 60,
  blockedPrefixes: Array.isArray(data.blockedPrefixes)
    ? data.blockedPrefixes.join('\n')
    : (data.blockedPrefixes || ''),
  emergencyPatterns: Array.isArray(data.emergencyPatterns)
    ? data.emergencyPatterns.join('\n')
    : (data.emergencyPatterns || ''),
});

const isVoiceNumber = (row) => {
  const caps = row?.capabilities || {};
  return Boolean(caps.voice || caps.sipCallerId);
};

const shareModeOf = (row) => String(row?.voiceShareMode || 'shared').toLowerCase();

const pickDefaultCallerId = (numbers, current, voiceMode) => {
  if (current) return current;
  const mode = voiceMode === 'dedicated' ? 'dedicated' : 'shared';
  const voiceOutbound = numbers.find(
    (n) => (n.defaultFor || []).includes('voiceOutbound')
      && n.e164
      && shareModeOf(n) === mode,
  );
  if (voiceOutbound?.e164) return voiceOutbound.e164;
  const firstVoice = numbers.find(
    (n) => isVoiceNumber(n) && n.status !== 'disabled' && n.e164 && shareModeOf(n) === mode,
  );
  return firstVoice?.e164 || '';
};

export default function PstnTab({ tenantId, tenantName = '', onNumbersChanged, canManage = false, canActivatePstn = false }) {
  const [subTab, setSubTab] = useState(0);
  const [numbersVersion, setNumbersVersion] = useState(0);

  const handleNumbersChanged = () => {
    setNumbersVersion((n) => n + 1);
    if (typeof onNumbersChanged === 'function') onNumbersChanged();
  };

  return (
    <Box>
      <Tabs
        value={subTab}
        onChange={(e, v) => setSubTab(v)}
        sx={{ borderBottom: '1px solid #e5e7eb', mb: 2 }}
      >
        <Tab label="Numbers" />
        <Tab label="Outbound" />
      </Tabs>
      {subTab === 0 && (
        <PhoneNumbersTab tenantId={tenantId} tenantName={tenantName} onNumbersChanged={handleNumbersChanged} canManage={canManage} />
      )}
      {subTab === 1 && (
        canActivatePstn ? (
          <PstnOutboundSettings
            tenantId={tenantId}
            numbersVersion={numbersVersion}
            canActivatePstn={canActivatePstn}
          />
        ) : (
          <TenantOutboundAllowancePreview />
        )
      )}
    </Box>
  );
}

const TENANT_OUTBOUND_ALLOWANCES = [
  {
    id: 'voice',
    title: 'Shared voice minutes',
    amount: '100',
    unit: 'minutes / month',
    detail: 'Outbound calling on the shared Signal Bridge line.',
  },
  {
    id: 'sms',
    title: 'Text messages',
    amount: '100',
    unit: 'messages / month',
    detail: 'SMS sent from this tenant’s assigned numbers.',
  },
  {
    id: 'email',
    title: 'Email',
    amount: '1,000',
    unit: 'messages / month',
    detail: 'Outbound email from tenant notifications and alerts.',
  },
];

function TenantOutboundAllowancePreview() {
  return (
    <div className="space-y-4" data-testid="tenant-outbound-allowance-preview">
      <div className={CARD_CLASS}>
        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Included usage</h3>
            <p className="mt-1 text-sm text-gray-600">
              Monthly allowances for this tenant on the shared Signal Bridge services.
            </p>
          </div>
          <span className="mt-2 inline-flex w-fit rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 sm:mt-0">
            Preview
          </span>
        </div>

        <dl className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {TENANT_OUTBOUND_ALLOWANCES.map((item) => (
            <div
              key={item.id}
              className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4"
              data-testid={`allowance-${item.id}`}
            >
              <dt className="text-sm font-medium text-gray-600">{item.title}</dt>
              <dd className="mt-2 flex items-baseline gap-2">
                <span className="text-3xl font-semibold tracking-tight text-gray-900">{item.amount}</span>
                <span className="text-sm text-gray-500">{item.unit}</span>
              </dd>
              <p className="mt-2 text-sm text-gray-600">{item.detail}</p>
            </div>
          ))}
        </dl>

        <p className="mt-5 text-xs text-gray-500">
          These figures are for planning only. They are not connected to billing, metering, or enforcement yet.
        </p>
      </div>
    </div>
  );
}

function PstnOutboundSettings({ tenantId, numbersVersion, canActivatePstn = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [voiceNumbers, setVoiceNumbers] = useState([]);

  const { register, handleSubmit, watch, setValue, reset, formState: { errors, isDirty } } = useForm({
    defaultValues: PSTN_DEFAULTS,
  });

  const enableOutboundPstn = watch('enableOutboundPstn');
  const voiceMode = watch('voiceMode');
  const callerIdFrom = watch('callerIdFrom');
  const emergencyPatterns = watch('emergencyPatterns');
  const e911Enabled = watch('e911Enabled');

  const callerIdOptions = useMemo(() => {
    const mode = voiceMode === 'dedicated' ? 'dedicated' : 'shared';
    if (voiceMode === 'none') return [];
    const eligible = voiceNumbers.filter(
      (n) => n.status !== 'disabled'
        && n.status !== 'suspended'
        && isVoiceNumber(n)
        && n.e164
        && shareModeOf(n) === mode,
    );
    const seen = new Set(eligible.map((n) => n.e164));
    if (callerIdFrom && !seen.has(callerIdFrom)) {
      return [
        { e164: callerIdFrom, label: `${formatE164Display(callerIdFrom)} (saved — reassign or change mode)` },
        ...eligible,
      ];
    }
    return eligible;
  }, [voiceNumbers, callerIdFrom, voiceMode]);

  const validationStatus = useMemo(() => {
    const issues = [];
    if (voiceMode !== 'none' && !callerIdFrom) {
      issues.push('Select an outbound caller ID from an assigned Voice number');
    }
    if (emergencyPatterns && !e911Enabled) {
      issues.push('E911 must be enabled when emergency patterns are configured');
    }
    return { isValid: issues.length === 0, issues };
  }, [voiceMode, callerIdFrom, emergencyPatterns, e911Enabled]);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [sipResp, numbersResp] = await Promise.all([
          fetchWithAuth(`/api/v2/tenants/${tenantId}/sip-config`).catch((err) => {
            if (err.status === 404 || err.message?.includes('SIP config not found')) return { data: {} };
            throw err;
          }),
          getTenantVoiceNumbers(tenantId).catch(() => []),
        ]);
        if (cancelled) return;
        const sipData = sipResp?.data || {};
        const list = Array.isArray(numbersResp?.data)
          ? numbersResp.data
          : Array.isArray(numbersResp)
            ? numbersResp
            : [];
        setVoiceNumbers(list);
        setConfig(sipData);
        const values = toFormValues(sipData);
        if (values.voiceMode !== 'none' && !values.callerIdFrom) {
          values.callerIdFrom = pickDefaultCallerId(list, '', values.voiceMode);
        }
        if (values.voiceMode !== 'none') {
          values.enableOutboundPstn = true;
        }
        reset(values);
      } catch (error) {
        console.error('Error loading PSTN settings:', error);
        toast.error('Failed to load PSTN settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, numbersVersion, reset]);

  useEffect(() => {
    if (voiceMode === 'none') {
      if (callerIdFrom) setValue('callerIdFrom', '', { shouldDirty: true });
      if (enableOutboundPstn) setValue('enableOutboundPstn', false, { shouldDirty: true });
      return;
    }
    setValue('enableOutboundPstn', true, { shouldDirty: true });
    if (callerIdFrom || !voiceNumbers.length) return;
    const next = pickDefaultCallerId(voiceNumbers, '', voiceMode);
    if (next) setValue('callerIdFrom', next, { shouldDirty: true });
  }, [voiceMode, callerIdFrom, voiceNumbers, enableOutboundPstn, setValue]);

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      setWarnings([]);
      const mode = data.voiceMode || 'none';
      const payload = {
        ...(config || {}),
        ...data,
        voiceMode: mode,
        enableOutboundPstn: mode !== 'none',
        callerIdFrom: mode === 'none' ? '' : data.callerIdFrom,
        tenantId,
        blockedPrefixes: toStringList(data.blockedPrefixes, config?.blockedPrefixes),
        emergencyPatterns: toStringList(data.emergencyPatterns, config?.emergencyPatterns),
      };
      if (typeof payload.autoAnswerExtensions === 'string') {
        payload.autoAnswerExtensions = toStringList(payload.autoAnswerExtensions, config?.autoAnswerExtensions);
      }
      if (typeof payload.ipAllowlist === 'string') {
        payload.ipAllowlist = toStringList(payload.ipAllowlist, config?.ipAllowlist);
      }

      const response = await fetchWithAuth(
        `/api/v2/tenants/${tenantId}/sip-config`,
        'PUT',
        payload
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to save PSTN settings');
      }

      toast.success('PSTN settings saved');
      const nextWarnings = (response.data?.warnings || []).filter(
        (w) => !/sessionTimer exceeds registrationExpiry/i.test(w)
      );
      setWarnings(nextWarnings);
      const saved = response.data?.config || payload;
      setConfig(saved);
      reset(toFormValues(saved));
    } catch (error) {
      console.error('Error saving PSTN settings:', error);
      toast.error(error.details || error.message || 'Failed to save PSTN settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gray-500 py-8">Loading PSTN settings...</div>;
  }

  return (
    <div className="space-y-6">
      {validationStatus.issues.length > 0 && (
        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
          <div className="flex">
            <FiAlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Configuration Issues</h3>
              <ul className="mt-2 list-disc pl-5 text-sm text-yellow-700 space-y-1">
                {validationStatus.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="rounded-md bg-blue-50 border border-blue-200 p-4">
          <div className="flex">
            <FiAlertCircle className="h-5 w-5 text-blue-400 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Warnings</h3>
              <ul className="mt-2 list-disc pl-5 text-sm text-blue-700 space-y-1">
                {warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <form className="space-y-6" onSubmit={canActivatePstn ? handleSubmit(onSubmit) : (e) => e.preventDefault()}>
        <fieldset disabled={!canActivatePstn} className="space-y-6">
        <div className={CARD_CLASS}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Voice mode
              </label>
              <select {...register('voiceMode')} className={SELECT_CLASS}>
                <option value="none">None — outbound PSTN disabled</option>
                <option value="shared">Shared — use selected shared Voice assignment</option>
                <option value="dedicated">Dedicated — use selected dedicated Voice assignment</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                None rejects outbound PSTN. Shared/Dedicated require an assigned Voice-enabled number with matching mode on the Numbers tab.
              </p>
              <input type="hidden" {...register('enableOutboundPstn')} />
            </div>

            {voiceMode !== 'none' && (
              <div className={NESTED_CALLOUT_CLASS}>
                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Outbound caller ID <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('callerIdFrom', {
                      required: voiceMode !== 'none' ? 'Required for PSTN' : false,
                    })}
                    className={SELECT_CLASS}
                  >
                    <option value="">
                      {callerIdOptions.length === 0
                        ? `Add a ${voiceMode} Voice number on the Numbers tab first`
                        : 'Select a number…'}
                    </option>
                    {callerIdOptions.map((n) => (
                      <option key={n.e164 || n.id} value={n.e164}>
                        {n.label || numberOptionLabel(n.e164, n.friendlyName || n.label)}
                      </option>
                    ))}
                  </select>
                  {errors.callerIdFrom && (
                    <p className="mt-1 text-xs text-red-600">{errors.callerIdFrom.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Only active Voice-enabled numbers assigned to this tenant (matching {voiceMode}). Stores E.164.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default country code
                  </label>
                  <input
                    type="text"
                    {...register('defaultCountryCode', {
                      required: 'Required',
                      pattern: { value: /^[A-Z]{2}$/, message: 'Must be ISO 3166-1 alpha-2 (e.g., US, CA)' },
                    })}
                    maxLength={2}
                    className={INPUT_CLASS}
                  />
                  {errors.defaultCountryCode && (
                    <p className="mt-1 text-xs text-red-600">{errors.defaultCountryCode.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Add prefix on outbound
                  </label>
                  <input
                    type="text"
                    {...register('addPrefixOutbound')}
                    placeholder="+1"
                    className={INPUT_CLASS}
                  />
                  <p className="mt-1 text-xs text-gray-500">E.164 prefix added to local numbers (e.g. +1)</p>
                </div>

                <div className={HIGHLIGHT_TOGGLE_CLASS}>
                  <input
                    type="checkbox"
                    {...register('intlAllowed')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">International dialing allowed</label>
                </div>

                <div className={HIGHLIGHT_TOGGLE_CLASS}>
                  <input
                    type="checkbox"
                    {...register('e911Enabled')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">E911 enabled</label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max call duration (seconds)
                  </label>
                  <input
                    type="number"
                    {...register('maxCallDuration', {
                      valueAsNumber: true,
                      min: { value: 30, message: 'Min 30' },
                      max: { value: 14400, message: 'Max 14400' },
                    })}
                    min={30}
                    max={14400}
                    className={INPUT_CLASS}
                  />
                  {errors.maxCallDuration && (
                    <p className="mt-1 text-xs text-red-600">{errors.maxCallDuration.message}</p>
                  )}
                </div>

                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Blocked prefixes</label>
                  <textarea
                    {...register('blockedPrefixes')}
                    rows={3}
                    placeholder={'+1900\n+1976'}
                    className={TEXTAREA_MONO_CLASS}
                  />
                  <p className="mt-1 text-xs text-gray-500">One E.164 prefix per line</p>
                </div>

                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emergency patterns</label>
                  <textarea
                    {...register('emergencyPatterns')}
                    rows={2}
                    placeholder={'^911$\n^112$'}
                    className={TEXTAREA_MONO_CLASS}
                  />
                  <p className="mt-1 text-xs text-gray-500">Regex patterns (one per line). Bypass blocks/rate limits.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={CARD_CLASS}>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Security & Abuse</h2>
          <p className="text-sm text-gray-500 mb-4">
            Cap how many calls a tenant user can start per minute. Applies to browser phone, panel PSTN, and SIP router dials.
          </p>
          <div className="grid grid-cols-2 gap-y-4 gap-x-6 sm:grid-cols-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate limit (calls/minute)
              </label>
              <input
                type="number"
                {...register('rateLimit', {
                  valueAsNumber: true,
                  min: { value: 10, message: 'Min 10' },
                  max: { value: 300, message: 'Max 300' },
                })}
                min={10}
                max={300}
                className={INPUT_CLASS}
              />
              {errors.rateLimit && (
                <p className="mt-1 text-xs text-red-600">{errors.rateLimit.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">Per tenant and caller. Skipped if Redis is unavailable.</p>
            </div>
          </div>
        </div>

        {canActivatePstn && (
        <div className="flex items-center justify-end space-x-3">
          {validationStatus.isValid ? (
            <div className="inline-flex items-center space-x-2 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-green-700">
              <FiCheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Ready</span>
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => reset(toFormValues(config || {}))}
            disabled={!isDirty || saving}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
          <button
            type="submit"
            disabled={saving || !validationStatus.isValid}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiSave className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save PSTN settings'}
          </button>
        </div>
        )}
        </fieldset>
      </form>
    </div>
  );
}
